import type { DriverRole, ExpiryState, FuelType, VehicleDocType } from '@mt/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** P12 B1 Đội xe (docs/phases/P12.md §6). */
export interface DocStatus {
  docType: VehicleDocType;
  label: string;
  expiresOn: string | null;
  docNo: string | null;
  daysLeft: number | null;
  state: ExpiryState;
}

export interface VehicleProfile {
  brand: string | null;
  model: string | null;
  manufactureYear: number | null;
  registeredPayloadKg: number | null;
  fuelType: FuelType;
  fuelNormLPer100km: number | null;
  /** P12 §16 (DEC-291): lít dầu / giờ cẩu (xe cẩu) */
  craneFuelLPerHour: number | null;
  tankLitres: number | null;
  odometerKm: number | null;
  vinNo: string | null;
  engineNo: string | null;
  inServiceOn: string | null;
  /** P11 B5: lòng thùng (m), khối xếp được (m³), phần hàng dài được thò ra sau (m) */
  cargoLengthM: number | null;
  cargoWidthM: number | null;
  cargoHeightM: number | null;
  usableVolumeM3: number | null;
  overhangMaxM: number | null;
}

export interface FleetVehicle {
  id: number;
  plateNo: string;
  name: string;
  isCrane: boolean;
  isDefault: boolean;
  isActive: boolean;
  maxLoadKg: number | null;
  sortOrder: number;
  note: string | null;
  version: number;
  profile: VehicleProfile;
  /** Khối xếp được dùng khi ghép chuyến, hàng dài nhất chở được; null = chưa đủ số */
  cargo: { volumeM3: number | null; maxItemLengthM: number | null };
  /** Điểm về của xe; null = dùng bãi chung / nơi làm việc (docs/phases/P12/diem-ve-xe.md) */
  home: { name: string | null; lat: number; lng: number; radiusM: number } | null;
  primaryDriver: { id: number; fullName: string; phone: string | null; since: string; assignmentId: number } | null;
  documents: DocStatus[];
  docState: ExpiryState;
  updatedAt: string;
}

export interface VehicleDocument {
  id: number;
  vehicleId: number;
  docType: VehicleDocType;
  title: string | null;
  docNo: string | null;
  provider: string | null;
  issuedOn: string | null;
  expiresOn: string;
  cost: number | null;
  note: string | null;
  version: number;
  createdByName: string | null;
  updatedAt: string;
}

export interface Assignment {
  id: number;
  vehicleId: number;
  plateNo: string | null;
  driverId: number;
  driverName: string | null;
  role: DriverRole;
  startOn: string;
  endOn: string | null;
  note: string | null;
  createdByName: string | null;
}

export interface OdometerCorrection {
  id: number;
  fromKm: number | null;
  toKm: number;
  reason: string;
  fixedRecords: number;
  byName: string | null;
  at: string;
}

export interface FleetVehicleDetail extends FleetVehicle {
  documentHistory: VehicleDocument[];
  assignments: Assignment[];
  odometerCorrections: OdometerCorrection[];
}

export interface Driver {
  id: number;
  fullName: string;
  phone: string | null;
  user: { id: number; username: string; fullName: string } | null;
  /** Mã nhân viên Asia (DEC-187) */
  asiaStaff: { code: string; name: string; kind: string } | null;
  idCardNo: string | null;
  licenseNo: string | null;
  licenseClass: string | null;
  licenseExpiresOn: string | null;
  licenseState: ExpiryState;
  craneCertNo: string | null;
  craneCertExpiresOn: string | null;
  craneCertState: ExpiryState | null;
  hiredOn: string | null;
  isActive: boolean;
  note: string | null;
  version: number;
  vehicle: { id: number; plateNo: string; role: DriverRole; since: string; assignmentId: number } | null;
  updatedAt: string;
}

export interface DriverDetail extends Driver {
  assignments: Assignment[];
}

export interface ExpiryItem {
  key: string;
  kind: 'vehicle_doc' | 'license' | 'crane_cert';
  label: string;
  vehicleId: number | null;
  plateNo: string | null;
  driverId: number | null;
  driverName: string | null;
  docType: VehicleDocType | null;
  expiresOn: string | null;
  daysLeft: number | null;
  state: ExpiryState;
}

export const FLEET_KEY = ['fleet'] as const;

export const useFleetVehicles = (enabled = true) => useQuery({ queryKey: [...FLEET_KEY, 'vehicles'], queryFn: () => api.get<FleetVehicle[]>('/fleet/vehicles'), enabled });
export const useFleetVehicle = (id: number | null) =>
  useQuery({ queryKey: [...FLEET_KEY, 'vehicle', id], queryFn: () => api.get<FleetVehicleDetail>(`/fleet/vehicles/${id}`), enabled: !!id });
export const useDrivers = () => useQuery({ queryKey: [...FLEET_KEY, 'drivers'], queryFn: () => api.get<Driver[]>('/fleet/drivers') });
export const useDriver = (id: number | null) =>
  useQuery({ queryKey: [...FLEET_KEY, 'driver', id], queryFn: () => api.get<DriverDetail>(`/fleet/drivers/${id}`), enabled: !!id });
export const useExpiring = () =>
  useQuery({ queryKey: [...FLEET_KEY, 'expiring'], queryFn: () => api.get<{ today: string; days: number; items: ExpiryItem[] }>('/fleet/expiring') });

const STATE_BADGE: Record<ExpiryState, { variant: 'success' | 'warning' | 'error' | 'neutral'; text: (d: number | null) => string }> = {
  ok: { variant: 'success', text: () => 'Còn hạn' },
  soon: { variant: 'warning', text: (d) => (d === 0 ? 'Hết hạn hôm nay' : `Còn ${d} ngày`) },
  expired: { variant: 'error', text: (d) => `Quá hạn ${d === null ? '' : -d} ngày` },
  missing: { variant: 'neutral', text: () => 'Chưa nhập' },
};
export const expiryBadge = (state: ExpiryState, daysLeft: number | null) => ({ variant: STATE_BADGE[state].variant, text: STATE_BADGE[state].text(daysLeft) });

/** Số ngày còn lại tính theo ngày trên máy (chỉ để hiển thị; server là nguồn sự thật). */
export function daysLeftLocal(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date();
  const today = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - today) / 86_400_000);
}

export const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Mã NV Asia (login / payroll) để gắn tài xế (DEC-187) */
export interface AsiaStaffOption {
  code: string;
  name: string;
  kind: 'login' | 'payroll';
  driver: { id: number; fullName: string } | null;
  user: { id: number; username: string } | null;
}
export const useAsiaStaffOptions = (enabled = true) =>
  useQuery({ queryKey: [...FLEET_KEY, 'asia-staff-options'], queryFn: () => api.get<AsiaStaffOption[]>('/fleet/asia-staff-options'), enabled });
